import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getAllConvo, markConversationAsRead } from '../../services/api';

// Async thunk for fetching conversations
export const fetchConversations = createAsyncThunk(
  'conversations/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAllConvo({});
      if (response.data.status) {
        return response.data.conversation;
      }
      throw new Error('Failed to fetch conversations');
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const markConversationRead = createAsyncThunk(
  'conversations/markRead',
  async (conversationId, { rejectWithValue }) => {
    try {
      await markConversationAsRead(conversationId);
      return conversationId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  conversations: [],
  selectedConversation: null,
  selectedUserName: '',
  loading: false,
  error: null,
  userMapping: {},
  unreadCounts: {}
};

const conversationSlice = createSlice({
  name: 'conversations',
  initialState,
  reducers: {
    setSelectedConversation: (state, action) => {
      state.selectedConversation = action.payload.conversationId;
      state.selectedUserName = action.payload.userName;
      // Reset unread count when selecting a conversation
      if (state.unreadCounts[action.payload.conversationId]) {
        state.unreadCounts[action.payload.conversationId] = 0;
      }
      // Also reset the conversation's unread count
      const conversation = state.conversations.find(conv => conv.id === action.payload.conversationId);
      if (conversation) {
        conversation.unread = 0;
      }
    },
    updateUserMapping: (state, action) => {
      state.userMapping = { ...state.userMapping, ...action.payload };
    },
    updateLastMessage: (state, action) => {
      const { conversationId, message, time, senderId, senderName, isFromCurrentUser } = action.payload;
      const conversation = state.conversations.find(conv => conv.id === conversationId);

      if (conversation) {
        // Update last message details
        conversation.lastMessage = message;
        conversation.lastMessageTime = time;
        conversation.time = new Date(time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

        // Update user mapping if we have a new sender name
        if (senderId && senderName && senderName !== 'Unknown' && !isFromCurrentUser) {
          state.userMapping[senderId] = senderName;
        }

        // Increment unread count if this is not the selected conversation and not from current user
        if (conversationId !== state.selectedConversation && !isFromCurrentUser) {
          state.unreadCounts[conversationId] = (state.unreadCounts[conversationId] || 0) + 1;
          conversation.unread = (conversation.unread || 0) + 1;
        }

        // Move conversation to top of list
        const index = state.conversations.findIndex(conv => conv.id === conversationId);
        if (index > -1) {
          const [updatedConv] = state.conversations.splice(index, 1);
          state.conversations.unshift(updatedConv);
        }
      } else {
        console.warn('ConversationSlice: Conversation not found for updateLastMessage:', conversationId);
      }
    },
    updateConversationUnreadCount: (state, action) => {
      const { conversationId, unreadCount } = action.payload;
      const conversation = state.conversations.find(conv => conv.id === conversationId);
      if (conversation) {
        conversation.unread = unreadCount || 0;
        state.unreadCounts[conversationId] = unreadCount || 0;
      }
    },
    clearConversationState: (state) => {
      return initialState;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConversations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.loading = false;
        // Format conversations and build user mapping
        const userMapping = {};

        state.conversations = action.payload.map(conv => {
          // Ensure we have the correct conversation ID
          const conversationId = conv._id;

          // Get the other user from testingMembers
          const otherUser = conv.testingMembers && conv.testingMembers[0];
          const userName = otherUser ?
            `${otherUser.firstName} ${otherUser.lastName}` :
            'Unknown User';

          // Update user mapping with both ID and name
          if (otherUser) {
            userMapping[otherUser._id] = userName;
          }

          return {
            id: conversationId, // Ensure consistent ID usage
            name: userName,
            lastMessage: conv.lastMessage || 'No messages yet',
            time: new Date(conv.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            unread: conv.unreadCount || conv.unread || 0,
            members: conv.members || [],
            userData: otherUser,
            updatedAt: conv.updatedAt,
            lastMessageTime: conv.lastMessageTime || conv.updatedAt,
            // Store additional mapping info
            memberIds: {
              otherUserId: otherUser?._id,
              currentUserId: conv.members?.find(id => id !== otherUser?._id)
            }
          };
        }).sort((a, b) => new Date(b.lastMessageTime) - new Date(a.lastMessageTime));

        // Update user mapping state
        state.userMapping = { ...state.userMapping, ...userMapping };

        // Initialize unread counts with proper conversation IDs
        state.unreadCounts = action.payload.reduce((acc, conv) => {
          acc[conv._id] = conv.unreadCount || conv.unread || 0;
          return acc;
        }, {});

      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(markConversationRead.fulfilled, (state, action) => {
        const conversationId = action.payload;
        const conversation = state.conversations.find(conv => conv.id === conversationId);
        if (conversation) conversation.unread = 0;
        state.unreadCounts[conversationId] = 0;
      });
}
});

export const {
  setSelectedConversation,
  updateUserMapping,
  updateLastMessage,
  updateConversationUnreadCount,
  clearConversationState
} = conversationSlice.actions;

export default conversationSlice.reducer; 